import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatTaka } from '@/utils/number';
import { returnPcmpMarketingPayment, returnPcmpProposalAccounts } from '../api/pcmpProposals.api';
import type { PcmpChannel, PcmpReturnPaymentResult } from '../types/pcmpProposal.types';
import { pcmpActionCallbacks } from '../utils/pcmpActionCallbacks';
import { usePcmpFileUploader } from './usePcmpFileUploader';

export interface PcmpReturnPaymentInput {
  /** Left out: everything that is left. */
  amount?: number | undefined;
  reason?: string | undefined;
  slip: File | null;
}

function successTitle(result: PcmpReturnPaymentResult): string {
  return result.closed
    ? 'Payment returned'
    : `Partially returned ${formatTaka(result.returnedAmount)} · ${formatTaka(result.remaining)} left`;
}

/**
 * Admin / MIS take a payout back, all or part, through the lane that paid it:
 * Accounts (LDS-765) or Marketing (LDS-766). The slip is uploaded first.
 */
export function useReturnPcmpPayment(
  proposalId: number,
  channel: PcmpChannel,
  onSuccess: () => void,
) {
  const queryClient = useQueryClient();
  const upload = usePcmpFileUploader();

  return useMutation({
    mutationFn: async ({ amount, reason, slip }: PcmpReturnPaymentInput) => {
      const payload = { amount, reason, slipKey: slip ? await upload(slip) : undefined };
      return channel === 'MARKETING'
        ? returnPcmpMarketingPayment(proposalId, payload)
        : returnPcmpProposalAccounts(proposalId, payload);
    },
    ...pcmpActionCallbacks(queryClient, successTitle, onSuccess),
  });
}
